import React, { SyntheticEvent, useRef, useState } from 'react';
import classnames from 'classnames';

import './options-button.scss';
import { FormattedMessage } from 'react-intl';
import { ButtonModifier, getBtnModifiersClasses } from '../../../infrastructure/helpers';
import { Popover, PopoverAlign, PopoverPosition } from '../popover/popover';
import ChevronSVG from '../../../images/svg/icons8-expand-arrow';

export interface IButtonOption {
    label: string;
    action: () => void;
    id?: string;
    danger?: boolean;
    disabled?: boolean;
    icon?: React.ComponentClass | React.FunctionComponent;
    setHandlerForClosePopup?: (handlerForCloseOptionsPopup: () => void) => void;
}

export interface ButtonProp {
    options: IButtonOption[];
    modifier?: ButtonModifier | ButtonModifier[];
    label?: string | JSX.Element;
    className?: string | null;
    icon?: React.ComponentClass | React.FunctionComponent;
    disabled?: boolean;
    isIconButton?: boolean;
    position?: PopoverPosition;
    align?: PopoverAlign;
    padding?: number;
    withChevron?: boolean;
    autoFlip?: boolean;
}

const OptionsButton = (props: ButtonProp) => {
    const [isOpen, toggleOptions] = useState(false);
    const [selectedOption, setSelectedOption] = useState<string>('');
    const buttonRef = useRef(null);

    const {
        options,
        modifier,
        label,
        className,
        disabled,
        isIconButton = false,
        withChevron = false,
        ...otherProps
    } = props;

    const btnClasses = classnames(
        'options-button',
        { 'options-button--active': isOpen },
        { 'icon-button': isIconButton },
        !isIconButton ? getBtnModifiersClasses(modifier) : '',
        className
    );

    const closePopover = (): void => {
        toggleOptions(false);
        setSelectedOption('');
    };

    return (
        <button
            className={btnClasses}
            disabled={disabled}
            ref={buttonRef}
            onBlur={closePopover}
            onClick={() => {
                toggleOptions(prev => {
                    if (selectedOption) setSelectedOption('');
                    return !prev;
                });
            }}
            type='button'
        >
         	<div className='options-button__content'>
	            {props.icon && <props.icon />}
	            { label && <span className='options-button__label'>{label}</span>}
	            { withChevron &&
	                <div className='options-button__chevron' >
	                    <ChevronSVG />
	                </div>
	            }
            </div>
	        <Popover
                buttonRef={buttonRef}
                isOpen={isOpen}
                {...otherProps}
            >
	                {options.map((option, i) => {
	                    return (
	                        <div
	                            className={
	                                classnames('options-button__option', {
	                                    'options-button__option--danger': option.danger,
	                                    'options-button__option--disabled': option.disabled,
	                                    'options-button__option--selected': option.label === selectedOption,
	                                })
	                            }
	                            key={i}
	                            onClick={(e: SyntheticEvent) => {
                                    e.stopPropagation();
                                    if (option.setHandlerForClosePopup) {
                                        setSelectedOption(option.label);
                                        option.setHandlerForClosePopup(closePopover);
                                    } else {
                                        closePopover();
                                    }
	                                if (!option.disabled) option.action();
	                            }}
	                        >
	                            {option.icon && <option.icon />}
	                            {option.id ? <FormattedMessage
                                    id={option.id}
                                    description='Button label'
                                    defaultMessage={option.label}
                                /> : option.label}
	                        </div>
	                    );
	                })}
	            </Popover>
        </button>
    );
};

export default OptionsButton;
